import { useCallback, useState } from 'react';

/**
 * Shared logic for Tab component (GL2025 theme)
 * Used by both web and native implementations
 */

/**
 * Get the appropriate style state based on tab state
 * @param {boolean} isCurrent - Whether this tab is currently selected
 * @param {boolean} isHovered - Whether tab is being hovered
 * @param {boolean} isFocused - Whether tab has keyboard focus
 * @param {boolean} isDisabled - Whether tab is disabled
 * @returns {string} State key for accessing theme styles
 */
export function getTabState(isCurrent, isHovered, isFocused, isDisabled) {
    if (isDisabled) return 'disabled';
    
    const stateType = isCurrent ? 'current' : 'inactive';
    
    if (isFocused) return `${stateType}.focus`;
    if (isHovered) return `${stateType}.hover`;
    return `${stateType}.default`;
}

/**
 * Custom hook for managing tab hover state
 * @returns {[boolean, function, function]} - [isHovered, handleMouseEnter, handleMouseLeave]
 */
export function useTabHover() {
    const [isHovered, setIsHovered] = useState(false);
    
    const handleMouseEnter = useCallback(() => {
        setIsHovered(true);
    }, []);
    
    const handleMouseLeave = useCallback(() => {
        setIsHovered(false);
    }, []);
    
    return [isHovered, handleMouseEnter, handleMouseLeave];
}

/**
 * Custom hook for managing tab focus state (keyboard-only)
 * Focus ring only shows for keyboard navigation, not mouse clicks
 * @returns {[boolean, function, function, function]} - [isFocused, handleFocus, handleBlur, handleMouseDown]
 */
export function useTabFocus() {
    const [isFocused, setIsFocused] = useState(false);
    const [isMouseDown, setIsMouseDown] = useState(false);
    
    const handleFocus = useCallback(() => {
        // Only show focus if it wasn't caused by mouse
        if (!isMouseDown) {
            setIsFocused(true);
        }
    }, [isMouseDown]);
    
    const handleBlur = useCallback(() => {
        setIsFocused(false);
    }, []);
    
    const handleMouseDown = useCallback(() => {
        setIsMouseDown(true);
        // Reset after a short delay to allow keyboard focus to work again
        setTimeout(() => setIsMouseDown(false), 0);
    }, []);
    
    return [isFocused, handleFocus, handleBlur, handleMouseDown];
}

/**
 * Hook to create click handler for tab
 * @param {function} onClick - Original click handler
 * @param {boolean} disabled - Whether tab is disabled
 * @param {number} tabIndex - Index of this tab
 * @returns {function} Click handler that respects disabled state
 */
export function useTabClickHandler(onClick, disabled, tabIndex) {
    return useCallback((e) => {
        if (!disabled && onClick) {
            onClick(tabIndex, e);
        }
    }, [onClick, disabled, tabIndex]);
}

/**
 * Hook to create keyboard handler for tab navigation
 * Handles arrow key navigation between tabs and moves focus to the newly selected tab
 * @param {number} tabIndex - Current tab index
 * @param {number} totalTabs - Total number of tabs
 * @param {function} onNavigate - Callback when navigating to different tab
 * @returns {function} Keyboard event handler
 */
export function useKeyboardNavigation(tabIndex, totalTabs, onNavigate) {
    return useCallback((e) => {
        if (!onNavigate || totalTabs <= 1) return;
        
        let nextIndex = -1;
        
        if (e.key === 'ArrowRight') {
            // Move to next tab, wrap to first if at end
            nextIndex = (tabIndex + 1) % totalTabs;
            e.preventDefault();
        } else if (e.key === 'ArrowLeft') {
            // Move to previous tab, wrap to last if at beginning
            nextIndex = (tabIndex - 1 + totalTabs) % totalTabs;
            e.preventDefault();
        }
        
        if (nextIndex !== -1 && nextIndex !== tabIndex) {
            onNavigate(nextIndex);
            
            // Move focus to the newly selected tab after state update
            setTimeout(() => {
                const currentTab = typeof e?.target?.closest === 'function' ? e.target.closest('[role="tablist"]') : null;
                if (currentTab) {
                    const tabs = currentTab.querySelectorAll('[role="tab"]');
                    const nextTab = tabs[nextIndex];
                    if (nextTab) {
                        nextTab.focus();
                    }
                }
            }, 0);
        }
    }, [tabIndex, totalTabs, onNavigate]);
}

/**
 * Get accessibility props for tab
 * @param {boolean} selected - Whether tab is selected
 * @param {string} panelId - ID of the associated panel
 * @param {number} tabIndex - Index of this tab
 * @returns {object} Accessibility props
 */
export function getAccessibilityProps(selected, panelId, tabIndex) {
    return {
        role: 'tab',
        'aria-selected': selected,
        'aria-controls': panelId || `tabpanel-${tabIndex}`,
        tabIndex: selected ? 0 : -1
    };
}

/**
 * Extract nested style value from theme
 * @param {object} theme - Theme object
 * @param {boolean} darkMode - Whether dark mode is active
 * @param {string} stateKey - State key (e.g., 'current.default', 'inactive.hover')
 * @param {string} styleType - 'container' or 'text'
 * @returns {object} Style object
 */
export function getStyleFromTheme(theme, darkMode, stateKey, styleType) {
    const mode = darkMode ? 'dark' : 'light';
    const statePath = stateKey.split('.');
    
    let styles = theme.tabs?.[mode];
    
    // Navigate through nested path (e.g., ['current', 'default'])
    for (const key of statePath) {
        styles = styles?.[key];
        if (!styles) break;
    }
    
    return styles?.[styleType] || {};
}
